import React, { useState, useEffect } from 'react';
import portfolioService, { ConversationSummary } from '../../services/portfolio';

interface Props {
  portfolioId: number;
  currentId: number | null;
  onSelect: (id: number) => void;
  onNew: () => void;
  onClose: () => void;
  onDeleted: (id: number) => void;
}

const fmtDate = (s: string) => {
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const ConversationsPanel: React.FC<Props> = ({ portfolioId, currentId, onSelect, onNew, onClose, onDeleted }) => {
  const [items, setItems] = useState<ConversationSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [renamingId, setRenamingId] = useState<number | null>(null);
  const [renameValue, setRenameValue] = useState('');

  const refresh = () =>
    portfolioService.listConversations(portfolioId).then(setItems).finally(() => setLoading(false));

  useEffect(() => { refresh(); }, [portfolioId]);

  const startRename = (c: ConversationSummary) => { setRenamingId(c.id); setRenameValue(c.title); };
  const commitRename = async (id: number) => {
    const title = renameValue.trim();
    setRenamingId(null);
    if (title) {
      await portfolioService.renameConversation(portfolioId, id, title);
      refresh();
    }
  };
  const remove = async (id: number) => {
    await portfolioService.deleteConversation(portfolioId, id);
    if (id === currentId) onDeleted(id);
    refresh();
  };

  const iconBtn: React.CSSProperties = {
    background: 'transparent', border: 'none', padding: '2px 4px', cursor: 'pointer', lineHeight: 1, fontSize: '0.85rem',
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,5,55,0.28)', zIndex: 1100,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 14, width: '100%', maxWidth: 480,
          maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
        }}
      >
        <div className="d-flex justify-content-between align-items-center" style={{ padding: '16px 18px 10px' }}>
          <span className="fw-semibold" style={{ fontSize: '0.95rem', color: '#000537' }}>Conversations</span>
          <button type="button" onClick={() => { onNew(); onClose(); }} className="btn d-inline-flex align-items-center gap-1"
            style={{ fontSize: '0.78rem', background: '#000537', color: '#fff', padding: '5px 12px' }}>
            ＋ New
          </button>
        </div>

        <div style={{ overflowY: 'auto', padding: '0 10px 12px', flex: 1 }}>
          {loading ? (
            <div style={{ fontSize: '0.85rem', color: '#a1a1aa', padding: '12px' }}>Loading…</div>
          ) : items.length === 0 ? (
            <div style={{ fontSize: '0.85rem', color: '#a1a1aa', padding: '12px' }}>No conversations yet.</div>
          ) : (
            items.map((c) => (
              <div
                key={c.id}
                className="d-flex align-items-center"
                style={{
                  padding: '8px 8px', borderRadius: 8, gap: 8,
                  background: c.id === currentId ? '#f4f4f5' : 'transparent',
                }}
              >
                {renamingId === c.id ? (
                  <input
                    autoFocus
                    className="form-control"
                    style={{ fontSize: '0.85rem', padding: '4px 8px' }}
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onBlur={() => commitRename(c.id)}
                    onKeyDown={(e) => { if (e.key === 'Enter') commitRename(c.id); }}
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => { onSelect(c.id); onClose(); }}
                    className="border-0 bg-transparent p-0 text-start flex-grow-1"
                    style={{ cursor: 'pointer', minWidth: 0 }}
                  >
                    <div style={{ fontSize: '0.85rem', color: '#18181b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {c.title}
                    </div>
                    <div style={{ fontSize: '0.7rem', color: '#a1a1aa' }}>
                      {fmtDate(c.updated_at)} · {c.message_count} message{c.message_count === 1 ? '' : 's'}
                    </div>
                  </button>
                )}
                <button type="button" onClick={() => startRename(c)} style={{ ...iconBtn, color: '#71717a' }} title="Rename">✎</button>
                <button type="button" onClick={() => remove(c.id)} style={{ ...iconBtn, color: '#b91c1c' }} title="Delete">×</button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ConversationsPanel;
